import type { KeyboardEvent, MouseEvent } from "react";
import { memo, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import {
  useCheckRaidTodo,
  useUpdateRaidMoreRewardCheck,
} from "@core/hooks/mutations/todo";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character, TodoRaid } from "@core/types/character";

import GoldText from "@components/todo/TodoList/text/GoldText";

interface Props {
  character: Character;
  /** 이 캐릭터가 이 주간 카테고리 레이드를 등록했으면 todo, 아니면 null */
  todo: TodoRaid | null;
}

/**
 * 주간 레이드 셀.
 * - todo가 null (캐릭터가 미등록): "-" 렌더
 * - 클릭: 1관문 체크 (allCheck=false)
 * - 우클릭: 전체 관문 올체크 (allCheck=true)
 * - "더보기" 버튼: 작은 팝오버로 더보기 체크 편집
 */
const RaidCell = ({ character, todo }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const checkRaidTodo = useCheckRaidTodo({
    onSuccess: (updated) => updateCharacterQueryData({ character: updated }),
  });

  const updateMoreReward = useUpdateRaidMoreRewardCheck({
    onSuccess: (updated) => updateCharacterQueryData({ character: updated }),
  });

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handler = (e: globalThis.MouseEvent) => {
      // "더보기" 버튼 영역 클릭은 외부 감지 제외 — 버튼의 onClick이 토글을 담당하므로
      // 여기서 먼저 닫으면 onClick이 곧바로 다시 열어 닫을 수 없는 버그 발생
      if ((e.target as HTMLElement)?.closest("[data-raid-more]")) return;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverOpen]);

  if (!todo) {
    return <Empty>-</Empty>;
  }

  const isDone = todo.currentGate === todo.totalGate;
  const moreRewardCompleted =
    todo.moreRewardCheckList.length > 0 &&
    todo.moreRewardCheckList.every((v) => v);
  const moreRewardPartial =
    !moreRewardCompleted &&
    todo.moreRewardCheckList.some((v) => v);

  const performCheck = (allCheck: boolean) => {
    checkRaidTodo.mutate({
      characterId: character.characterId,
      weekCategory: todo.weekCategory,
      allCheck,
    });
  };

  const handleCheck = (e: MouseEvent<HTMLDivElement>) => {
    // 더보기 버튼 클릭은 제외
    if ((e.target as HTMLElement).closest("[data-raid-more]")) return;
    performCheck(false);
  };

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    performCheck(true);
  };

  // 키보드 접근성: Enter = 1관문 체크, Space = 전체 올체크 (우클릭과 동일)
  // 더보기 영역에 포커스가 있을 때는 자체 체크박스 제어를 방해하지 않음
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("[data-raid-more]")) return;
    if (e.key === "Enter") {
      e.preventDefault();
      performCheck(false);
    } else if (e.key === " ") {
      e.preventDefault();
      performCheck(true);
    }
  };

  const handleToggleMoreReward = (gateIndex: number) => {
    updateMoreReward.mutate({
      characterId: character.characterId,
      weekCategory: todo.weekCategory,
      gate: gateIndex + 1,
    });
  };

  const { showMoreButton } = character.settings;

  return (
    <Wrapper
      onClick={handleCheck}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      $isDone={isDone}
      role="button"
      tabIndex={0}
      title="클릭(Enter): 1관문 체크, 우클릭(Space): 전체 올체크"
    >
      <GateBar $total={todo.totalGate} $current={todo.currentGate}>
        {Array.from({ length: todo.totalGate }, (_, i) => (
          <GateSeg key={i} $filled={i < todo.currentGate} />
        ))}
      </GateBar>
      <GoldLine>
        <GoldText>{todo.realGold}</GoldText>
        {todo.characterGold !== 0 && (
          <CharGold>{todo.characterGold}G</CharGold>
        )}
      </GoldLine>
      {showMoreButton && todo.moreRewardCheckList.length > 0 && (
        <MoreRow data-raid-more>
          <MoreButton
            type="button"
            $completed={moreRewardCompleted}
            $partial={moreRewardPartial}
            onClick={(e) => {
              e.stopPropagation();
              setPopoverOpen((v) => !v);
            }}
          >
            더보기
            {moreRewardCompleted && " ✓"}
          </MoreButton>
          {popoverOpen && (
            <Popover
              ref={popoverRef}
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.stopPropagation()}
            >
              <PopoverTitle>더보기 체크</PopoverTitle>
              {todo.moreRewardCheckList.map((checked, i) => {
                const gateCleared = i < todo.currentGate;
                return (
                  <PopoverItem key={i}>
                    <input
                      type="checkbox"
                      id={`rc-${character.characterId}-${todo.weekCategory}-${i}`}
                      checked={checked}
                      disabled={!gateCleared}
                      onChange={() => {
                        if (gateCleared) handleToggleMoreReward(i);
                      }}
                    />
                    <label
                      htmlFor={`rc-${character.characterId}-${todo.weekCategory}-${i}`}
                    >
                      {i + 1}관문
                      {!gateCleared && " (미클리어)"}
                    </label>
                  </PopoverItem>
                );
              })}
            </Popover>
          )}
        </MoreRow>
      )}
    </Wrapper>
  );
};

export default memo(RaidCell);

const Wrapper = styled.div<{ $isDone: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 3px;
  padding: 6px 8px;
  min-height: 54px;
  cursor: pointer;
  opacity: ${({ $isDone }) => ($isDone ? 0.4 : 1)};

  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const GateBar = styled.div<{ $total: number; $current: number }>`
  display: flex;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 3px;
  height: 12px;
  overflow: hidden;
`;

const GateSeg = styled.div<{ $filled: boolean }>`
  flex: 1;
  background: ${({ $filled, theme }) =>
    $filled ? theme.app.gauge.red : "transparent"};

  & + & {
    border-left: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const GoldLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
`;

const CharGold = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.app.text.black};
  font-size: 10px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.app.palette.yellow[450]};
  line-height: 1;
`;

const MoreRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const MoreButton = styled.button<{ $completed: boolean; $partial: boolean }>`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  border: 1px solid
    ${({ $completed, $partial, theme }) =>
      $completed
        ? theme.app.palette.red[200]
        : $partial
          ? theme.app.palette.yellow[350]
          : theme.app.border};
  background: ${({ $completed, $partial, theme }) =>
    $completed
      ? theme.app.palette.red[200]
      : $partial
        ? theme.app.palette.yellow[200]
        : "transparent"};
  color: ${({ $completed, theme }) =>
    $completed ? theme.app.palette.gray[0] : theme.app.text.dark2};
  cursor: pointer;
  line-height: 1.2;

  &:hover {
    opacity: 0.85;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  margin-top: 4px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.app.bg.white};
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  min-width: 120px;
`;

const PopoverTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.light2};
  margin-bottom: 4px;
`;

const PopoverItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;

  input {
    cursor: pointer;
  }

  label {
    cursor: pointer;
    color: ${({ theme }) => theme.app.text.dark2};
  }

  input:disabled + label {
    color: ${({ theme }) => theme.app.text.light2};
    cursor: not-allowed;
  }
`;

const Empty = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 54px;
  color: ${({ theme }) => theme.app.text.light2};
  font-size: 13px;
`;
